import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsUpDownIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  bedarfAnstehend,
  getMerosUebersicht,
  merosZustandLabel,
  type MerosBedarf,
  type MerosUebersicht,
} from "@/lib/meros";
import { fetchVeranstaltungUngelesenCount } from "@/lib/veranstaltungen";
import { MerosAbweichungBadge, MerosZustandBadge } from "@/pages/veranstaltungen/MerosZustandBadge";

const CARD_CLASS =
  "group relative flex h-full min-h-[9.5rem] flex-col gap-3 overflow-hidden rounded-ios border border-separator bg-card p-5 shadow-ios transition hover:-translate-y-0.5 hover:border-accent hover:shadow-ios-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function ortZeile(item: MerosBedarf): string {
  const parts: string[] = [];
  for (const value of [item.venue, item.bereich, item.ort]) {
    const text = value.trim();
    if (!text || parts.includes(text)) continue;
    parts.push(text);
  }
  return parts.join(" · ");
}

export function VeranstaltungenUebersichtPage() {
  const [data, setData] = useState<MerosUebersicht | null>(null);
  const [offene, setOffene] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all([getMerosUebersicht(), fetchVeranstaltungUngelesenCount()])
      .then(([uebersicht, count]) => {
        if (cancelled) return;
        setData(uebersicht);
        setOffene(count);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Die Übersicht konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const anstehend = (data?.bedarf || []).filter((item) => bedarfAnstehend(item));
  const naechster = anstehend.find((item) => item.zustand) || null;
  const aktuell = data?.aktuell || null;
  const abweichung = Boolean(
    naechster?.abweichung ??
      (aktuell && naechster?.zustand && naechster.zustand !== aktuell.zustand),
  );

  return (
    <AppShell
      title="Veranstaltungen"
      subtitle="Messe-Einsätze und der Zustand der Merostützen"
    >
      <div className="space-y-4">
        {error ? <Alert variant="warning">{error}</Alert> : null}

        <Card title="Merostützen">
          {loading ? (
            <Spinner className="h-5 w-5" />
          ) : error ? (
            <p className="text-sm text-label-secondary">Der Zustand konnte nicht geladen werden.</p>
          ) : (
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  Aktueller Zustand
                </p>
                <div className="mt-2">
                  {aktuell ? (
                    <MerosZustandBadge zustand={aktuell.zustand} prefix="" size="lg" />
                  ) : (
                    <span className="text-sm text-label-secondary">Noch nicht erfasst</span>
                  )}
                </div>
                {naechster ? (
                  <p className={`mt-3 max-w-xl text-sm ${abweichung ? "text-status-orange" : "text-label-secondary"}`}>
                    {naechster.event_title || "Nächste Veranstaltung"} benötigt die Merostützen{" "}
                    <strong>{merosZustandLabel(naechster.zustand)}</strong>
                    {naechster.datum ? ` (${naechster.datum})` : ""}.
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-label-secondary">
                    In den gelesenen Veranstaltungen ist noch kein benötigter Zustand genannt.
                  </p>
                )}
              </div>
              <Link to="/veranstaltungen/meros" className="text-sm font-medium text-accent hover:underline">
                Zustand erfassen
              </Link>
            </div>
          )}
        </Card>

        <section aria-label="Bereiche" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Link to="/veranstaltungen/messe" className={CARD_CLASS}>
            <div className="flex items-start justify-between gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
                <CalendarDaysIcon className="h-[22px] w-[22px]" aria-hidden />
              </span>
              <ChevronRightIcon className="mt-1 h-5 w-5 text-label-tertiary" aria-hidden />
            </div>
            <div>
              <h2 className="text-base font-semibold text-label">Veranstaltungen Messe</h2>
              <p className="mt-1 text-sm leading-snug text-label-secondary">
                Regieprotokolle aus dem Messe-Postfach. Dort steht, in welchem Zustand die
                Merostützen bei der Veranstaltung benötigt werden.
              </p>
            </div>
            <p className="mt-auto text-xs font-medium text-label-tertiary">
              {offene == null
                ? "Einsätze lesen"
                : offene === 1
                  ? "1 offener Einsatz"
                  : `${offene} offene Einsätze`}
            </p>
          </Link>

          <Link to="/veranstaltungen/meros" className={CARD_CLASS}>
            <div className="flex items-start justify-between gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
                <ArrowsUpDownIcon className="h-[22px] w-[22px]" aria-hidden />
              </span>
              <ChevronRightIcon className="mt-1 h-5 w-5 text-label-tertiary" aria-hidden />
            </div>
            <div>
              <h2 className="text-base font-semibold text-label">Meros</h2>
              <p className="mt-1 text-sm leading-snug text-label-secondary">
                Den tatsächlichen Zustand der Merostützen festhalten und mit dem Bedarf der
                Veranstaltungen vergleichen.
              </p>
            </div>
            <p className="mt-auto text-xs font-medium text-label-tertiary">
              {aktuell ? `Zuletzt ${merosZustandLabel(aktuell.zustand)}` : "Zustand noch offen"}
            </p>
          </Link>
        </section>

        {anstehend.length > 0 ? (
          <Card title="Benötigter Zustand" description="Aus den aktuellen Regieplänen.">
            <ul className="divide-y divide-separator">
              {anstehend.slice(0, 6).map((item, index) => (
                <li key={`${item.mail_id}-${index}`} className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium text-label">{item.event_title || "Veranstaltung"}</p>
                    <p className="mt-0.5 text-sm text-label-secondary">
                      {[item.datum, ortZeile(item), item.kategorie].filter(Boolean).join(" · ") || item.aufgabe}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <MerosZustandBadge zustand={item.zustand} />
                    {item.abweichung ? (
                      <MerosAbweichungBadge ist={item.ist_zustand || aktuell?.zustand} />
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </AppShell>
  );
}
